import { API_CONFIG } from '../config/api';

export type InvestingGoal = 'GROWTH' | 'INCOME' | 'PRESERVATION' | 'LEARNING';
export type RiskLevel = 'low' | 'medium' | 'high';

export interface UserPreferencesSummary {
  investingGoal: InvestingGoal | null;
  riskLevel: RiskLevel | null;
  onboardingCompletedAt: string | null;
  firstRunDismissedAt: string | null;
  notifyBilling: boolean;
  notifyProduct: boolean;
  notifyScoreboard: boolean;
  notifyMarketing: boolean;
  notifyInApp: boolean;
}

export interface CompleteOnboardingRequest {
  investingGoal: InvestingGoal;
  riskLevel: RiskLevel;
}

export interface UpdatePreferencesRequest {
  investingGoal?: InvestingGoal;
  riskLevel?: RiskLevel;
  notifyProduct?: boolean;
  notifyScoreboard?: boolean;
  notifyMarketing?: boolean;
  notifyInApp?: boolean;
  firstRunDismissedAt?: string | null;
}

export const onboardingApi = {
  /**
   * Get preference fields (onboarding + notifications).
   */
  getPreferences: async (token: string): Promise<UserPreferencesSummary> => {
    const response = await fetch(`${API_CONFIG.BASE_URL}/preferences`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to fetch preferences');
    }

    return response.json();
  },

  /**
   * Partial update for investing profile / notification prefs / first-run flag.
   */
  updatePreferences: async (
    token: string,
    body: UpdatePreferencesRequest,
  ): Promise<UserPreferencesSummary> => {
    const response = await fetch(`${API_CONFIG.BASE_URL}/preferences`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to update preferences');
    }

    return response.json();
  },

  /**
   * Single write at the end of the onboarding wizard (no per-step PATCH).
   */
  completeOnboarding: async (
    token: string,
    body: CompleteOnboardingRequest,
  ): Promise<UserPreferencesSummary> => {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/preferences/onboarding/complete`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to complete onboarding');
    }

    return response.json();
  },
};
